import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { keyframes, Stack, styled, Tooltip, Typography } from '@mui/material';
import classNames from 'classnames';

import { Flag, FlagProps, Icon, IconOption, ListSubheader, Menu, pulseGlow } from '@components/base';
import useSearchContext from '@hooks/useSearchContext';
import { Countries, SearchLanguages } from '@models/CountriesAndLanguages';
import { globalVarsActions, shouldNudgeCountrySelector } from '@store/globalVars/slice';
import { useAppDispatch, useAppSelector } from '@store/hooks';

const bounce = keyframes`
  from, 20%, 50%, 80%, to {
    transform: translateY(0);
  }

  40% {
    transform: translateY(-8px);
  }

  60% {
    transform: translateY(-4px);
  }
`;

const StyledChip = styled(Menu.ChipButton)`
  &.highlight {
    animation:
      ${({ theme }) => pulseGlow(theme.palette.error.main)} 1.5s ease infinite,
      ${bounce} 1.5s ease infinite;
  }
`;

type CountrySelectorProps<T extends FlagProps['country']> = {
  getOptionLabel: (option: T) => string;
  heading: string;
  highlight?: boolean;
  hint: string;
  icon: IconOption;
  options: T[];
  setValue: (value: T) => void;
  value: T;
};

function CountrySelector<T extends FlagProps['country']>({
  getOptionLabel,
  heading,
  highlight,
  hint,
  icon,
  options,
  setValue,
  value,
}: CountrySelectorProps<T>) {
  const dispatch = useAppDispatch();
  const disableHighlight = () => dispatch(globalVarsActions.clearFetchTitleStatuses());

  return (
    <Menu>
      <Tooltip title={hint}>
        <StyledChip
          className={classNames({ highlight: highlight && icon === 'public' })}
          icon={<Icon fontSize='small' name={icon} />}
          label={
            <Typography
              alignItems='center'
              component='span'
              display='inline-flex'
              flexDirection='row'
              gap={0.75}
              mr={-0.5}
              variant='inherit'
            >
              {getOptionLabel(value)}
              <Flag country={value} size='tiny' variant='circle' />
            </Typography>
          }
          onFocus={disableHighlight}
          onMouseEnter={disableHighlight}
          size='medium'
        />
      </Tooltip>

      <Menu.Content>
        <ListSubheader>{heading}</ListSubheader>

        {options.map(option => (
          <Menu.Item
            icon={<Flag country={option} variant='standard' />}
            key={option}
            onClick={() => setValue(option)}
            selected={option === value}
          >
            {getOptionLabel(option)}
          </Menu.Item>
        ))}
      </Menu.Content>
    </Menu>
  );
}

export const LocalizationSelector: FC = () => {
  const { country, searchLanguage, setCountry, setSearchLanguage } = useSearchContext();
  const { t } = useTranslation();

  const nudgeCountrySelector = useAppSelector(shouldNudgeCountrySelector);

  return (
    <Stack direction='row' spacing={0.5}>
      <CountrySelector
        getOptionLabel={language => t('language', { context: language.toLowerCase() })}
        heading={t('contentLanguage.select')}
        hint={t('contentLanguage.change')}
        icon='translate'
        options={[...SearchLanguages]}
        setValue={setSearchLanguage}
        value={searchLanguage}
      />

      <CountrySelector
        getOptionLabel={country => t('country', { context: country.toLowerCase() })}
        heading={t('contentLocation.select')}
        highlight={nudgeCountrySelector}
        hint={t('contentLocation.change')}
        icon='public'
        options={[...Countries]}
        setValue={setCountry}
        value={country}
      />
    </Stack>
  );
};
